import React from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock,
  Film,
  Heart,
  MapPin,
  Navigation,
  Send,
  Share2,
  Sparkles,
  X,
} from 'lucide-react';
import type { InvitationData, TemplateMetadata } from '../types';
import { registry } from '../registry';
import './theme.css';

const ASSET_BASE = '/assets/templates/video-03-radha-krishna';

const META: TemplateMetadata = {
  id: 'video-03-radha-krishna',
  name: 'Radha Krishna Garden Wedding Video',
  occasionType: 'WEDDING',
  description:
    'A cinematic Radha Krishna inspired wedding invitation built from PSD scene boards, ambient flower and gold particle motion overlays, dynamic ceremony details, RSVP, map directions, photo showcases, and pre-wedding video support.',
  isPremium: true,
  tags: [
    'video',
    'radha-krishna',
    'garden',
    'mandap',
    'wedding',
    'devotional',
    'cinematic',
    'animated',
    'scroll-experience',
    'premium',
    'haldi',
    'sangeet',
    'reception',
  ],
  colorScheme: { primary: '#F4C95D', accent: '#B83278', bg: '#171021' },
};

type Scene = {
  id: string;
  shortName: string;
  title: string;
  devotionalTitle: string;
  image: string;
  defaultDate: string;
  defaultTime: string;
  copy: string;
  tint: string;
};

const SCENES: Scene[] = [
  {
    id: 'ganesha',
    shortName: 'Blessing',
    title: 'Shree Ganesha Blessing',
    devotionalTitle: 'श्री गणेशाय नमः',
    image: `${ASSET_BASE}/ganesha-blessing.webp`,
    defaultDate: 'Wednesday, 29 October 2025',
    defaultTime: '09:00 AM',
    copy: 'With auspicious blessings, the celebration begins in devotion and joy.',
    tint: 'from-[#2d1735]/82 via-[#3d1831]/62 to-[#6e2418]/72',
  },
  {
    id: 'welcome',
    shortName: 'Mandap',
    title: 'Garden Mandap Welcome',
    devotionalTitle: 'स्वागत समारंभ',
    image: `${ASSET_BASE}/mandap-forest.webp`,
    defaultDate: 'Thursday, 30 October 2025',
    defaultTime: '05:30 PM',
    copy: 'A floral mandap, quiet lake breeze, and family blessings welcome everyone home.',
    tint: 'from-[#1d352b]/84 via-[#436d5c]/52 to-[#5c2f47]/68',
  },
  {
    id: 'haldi',
    shortName: 'Haldi',
    title: 'Haldi & Sangeet Utsav',
    devotionalTitle: 'हळदी व संगीत उत्सव',
    image: `${ASSET_BASE}/haldi-procession.webp`,
    defaultDate: 'Friday, 31 October 2025',
    defaultTime: '10:00 AM',
    copy: 'Golden haldi, folk music, and dancing families fill the day with color.',
    tint: 'from-[#835213]/76 via-[#ddad31]/38 to-[#592337]/66',
  },
  {
    id: 'mehendi',
    shortName: 'Mehendi',
    title: 'Mehendi Garden Portraits',
    devotionalTitle: 'मेहंदीची संध्या',
    image: `${ASSET_BASE}/green-couple.webp`,
    defaultDate: 'Friday, 31 October 2025',
    defaultTime: '04:30 PM',
    copy: 'A lush green evening for mehendi, laughter, portraits, and warm wishes.',
    tint: 'from-[#153626]/82 via-[#27613f]/48 to-[#091b18]/74',
  },
  {
    id: 'sangeet',
    shortName: 'Sangeet',
    title: 'Moonlit Sangeet Night',
    devotionalTitle: 'संगीत रात्र',
    image: `${ASSET_BASE}/blue-couple.webp`,
    defaultDate: 'Friday, 31 October 2025',
    defaultTime: '07:30 PM',
    copy: 'A moon-blue night of music, dance, and celebration for both families.',
    tint: 'from-[#0c2245]/84 via-[#1e4776]/44 to-[#441d55]/68',
  },
  {
    id: 'family',
    shortName: 'Family',
    title: 'Family Celebration',
    devotionalTitle: 'कुटुंबीयांचा स्नेह',
    image: `${ASSET_BASE}/yellow-couple.webp`,
    defaultDate: 'Saturday, 1 November 2025',
    defaultTime: '11:00 AM',
    copy: 'A soft festive morning for blessings, photographs, and family rituals.',
    tint: 'from-[#72510e]/72 via-[#ffdc74]/28 to-[#5b331d]/66',
  },
  {
    id: 'wedding',
    shortName: 'Vivah',
    title: 'Shubh Vivah Ceremony',
    devotionalTitle: 'शुभ विवाह सोहळा',
    image: `${ASSET_BASE}/royal-couple.webp`,
    defaultDate: 'Saturday, 1 November 2025',
    defaultTime: '12:35 PM',
    copy: 'The vows are taken with grace, devotion, and the blessings of elders.',
    tint: 'from-[#4b173c]/76 via-[#ce5b8e]/32 to-[#40204f]/68',
  },
  {
    id: 'radha-krishna',
    shortName: 'Blessings',
    title: 'Radha Krishna Blessings',
    devotionalTitle: 'राधा कृष्ण आशीर्वाद',
    image: `${ASSET_BASE}/radha-krishna-garden.webp`,
    defaultDate: 'Saturday, 1 November 2025',
    defaultTime: '07:30 PM',
    copy: 'The evening closes with divine love, blessings, and a joyful reception.',
    tint: 'from-[#163d27]/84 via-[#568c58]/36 to-[#224c6b]/66',
  },
];

function valueFrom(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function formatDate(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function getWeddingDisplayData(data: InvitationData) {
  const customData = (data.customData ?? {}) as any;
  const weddingData = customData.wedding ?? {};
  const expMeta = customData.experience?.meta ?? {};
  const referenceImages = expMeta.referenceImages ?? {};

  const groomName = valueFrom(
    weddingData.groom?.fullName,
    weddingData.groom?.name,
    weddingData.groom?.nickname,
    expMeta.groomName,
    expMeta.secondaryName,
    customData.groomName,
    customData.groom?.name,
    'Raghav',
  );
  const brideName = valueFrom(
    weddingData.bride?.fullName,
    weddingData.bride?.name,
    weddingData.bride?.nickname,
    expMeta.brideName,
    expMeta.primaryName,
    customData.brideName,
    customData.bride?.name,
    'Riya',
  );
  const eventTitle = valueFrom(data.eventName, expMeta.eventTitle, `${groomName} & ${brideName}`);
  const hostNames = valueFrom(data.hostName, expMeta.hostName, customData.hostNames, customData.hostFamily, 'Chopra Pariwar');
  const eventDate = valueFrom((data as any)._dateInput, expMeta.weddingDate, expMeta.eventDate, formatDate(data.eventDate));
  const eventTime = valueFrom((data as any)._timeInput, data.eventTime, customData.eventTime, expMeta.eventTime, '06:00 PM onwards');
  const venueName = valueFrom(data.eventVenue, expMeta.venueName, customData.venueName, customData.venueAddress, 'Vrindavan Garden Banquets');
  const venueAddress = valueFrom(customData.venueAddress, expMeta.venueAddress, data.eventVenue, 'Vrindavan Garden Banquets, Pune');
  const tagline = valueFrom(
    expMeta.tagline,
    data.customMessage,
    'Two hearts meet under the grace of Radha Krishna.',
  );

  return {
    groomName,
    brideName,
    eventTitle,
    hostNames,
    eventDate,
    eventTime,
    venueName,
    venueAddress,
    tagline,
    bridePhoto: valueFrom(referenceImages.bride, expMeta.bridePhotoUrl, expMeta.bridePhoto, customData.bridePhotoUrl, customData.bridePhoto),
    groomPhoto: valueFrom(referenceImages.groom, expMeta.groomPhotoUrl, expMeta.groomPhoto, customData.groomPhotoUrl, customData.groomPhoto),
    couplePhoto: valueFrom(referenceImages.couple, referenceImages.primary, expMeta.couplePhotoUrl, expMeta.couplePhoto, customData.couplePhotoUrl, customData.couplePhoto),
    customVideoUrl: valueFrom(customData.customVideoUrl, expMeta.customVideoUrl, customData.preWeddingVideoUrl, expMeta.preWeddingVideoUrl),
  };
}

function PhotoShowcase({
  brideName,
  groomName,
  bridePhoto,
  groomPhoto,
  couplePhoto,
}: {
  brideName: string;
  groomName: string;
  bridePhoto: string;
  groomPhoto: string;
  couplePhoto: string;
}) {
  if (!bridePhoto && !groomPhoto && !couplePhoto) return null;

  return (
    <div className="mt-6 grid grid-cols-2 gap-4">
      {groomPhoto && (
        <div className="radha-glass rounded-2xl p-3 text-center">
          <div className="mx-auto h-24 w-24 overflow-hidden rounded-full border-2 border-[#f6d978] bg-[#201226] shadow-[0_0_22px_rgba(246,217,120,0.36)]">
            <img src={groomPhoto} alt={groomName} className="h-full w-full object-cover" />
          </div>
          <p className="mt-2 text-sm font-semibold text-[#ffe7a1]">{groomName}</p>
          <p className="text-xs text-[#ffc9d6]">Groom</p>
        </div>
      )}
      {bridePhoto && (
        <div className="radha-glass rounded-2xl p-3 text-center">
          <div className="mx-auto h-24 w-24 overflow-hidden rounded-full border-2 border-[#f6d978] bg-[#201226] shadow-[0_0_22px_rgba(246,217,120,0.36)]">
            <img src={bridePhoto} alt={brideName} className="h-full w-full object-cover" />
          </div>
          <p className="mt-2 text-sm font-semibold text-[#ffe7a1]">{brideName}</p>
          <p className="text-xs text-[#ffc9d6]">Bride</p>
        </div>
      )}
      {couplePhoto && !bridePhoto && !groomPhoto && (
        <div className="col-span-2 radha-glass rounded-2xl p-3 text-center">
          <div className="h-52 overflow-hidden rounded-2xl border border-[#f6d978]/70 bg-[#201226]">
            <img src={couplePhoto} alt={`${groomName} and ${brideName}`} className="h-full w-full object-cover" />
          </div>
          <p className="mt-2 text-sm font-semibold text-[#ffe7a1]">
            {groomName} &amp; {brideName}
          </p>
        </div>
      )}
    </div>
  );
}

export function VideoRadhaKrishnaTemplate({ data }: { data: InvitationData }) {
  const {
    groomName,
    brideName,
    eventTitle,
    hostNames,
    eventDate,
    eventTime,
    venueName,
    venueAddress,
    tagline,
    bridePhoto,
    groomPhoto,
    couplePhoto,
    customVideoUrl,
  } = getWeddingDisplayData(data);

  const [activeSceneId, setActiveSceneId] = React.useState(SCENES[1].id);
  const [showRsvpModal, setShowRsvpModal] = React.useState(false);
  const [showShareToast, setShowShareToast] = React.useState(false);

  const activeScene = SCENES.find((scene) => scene.id === activeSceneId) ?? SCENES[1];

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const sceneId = visible?.target.getAttribute('data-scene-id');
        if (sceneId) setActiveSceneId(sceneId);
      },
      { threshold: [0.28, 0.45, 0.62], rootMargin: '-8% 0px -20% 0px' },
    );

    const sections = document.querySelectorAll<HTMLElement>('[data-radha-scene]');
    sections.forEach((section) => observer.observe(section));
    return () => sections.forEach((section) => observer.unobserve(section));
  }, []);

  const scrollToScene = (id: string) => {
    document.getElementById(`radha-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleShare = () => {
    const title = `Wedding Invitation: ${groomName} & ${brideName}`;
    if (navigator.share) {
      void navigator.share({
        title,
        text: `You are invited to the wedding celebration of ${groomName} and ${brideName}.`,
        url: window.location.href,
      }).catch(() => undefined);
      return;
    }

    void navigator.clipboard?.writeText(window.location.href);
    setShowShareToast(true);
    window.setTimeout(() => setShowShareToast(false), 2600);
  };

  return (
    <div className="radha-template relative isolate min-h-screen w-full overflow-hidden text-[#fff6dc]" data-screen-label="video-03-radha-krishna">
      <div className="absolute inset-0 -z-20 overflow-hidden bg-[#171021]">
        {SCENES.map((scene) => (
          <img
            key={scene.id}
            src={scene.image}
            alt=""
            aria-hidden="true"
            className={`radha-scene-image absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700 ${
              scene.id === activeScene.id ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
        <div className={`absolute inset-0 bg-gradient-to-b ${activeScene.tint} transition-colors duration-700`} />
        <video
          autoPlay
          loop
          muted
          playsInline
          src={`${ASSET_BASE}/gold-particles.mp4`}
          className="absolute inset-0 h-full w-full object-cover opacity-40 mix-blend-screen"
        />
        <video
          autoPlay
          loop
          muted
          playsInline
          src={`${ASSET_BASE}/flower-overlay.mp4`}
          className="absolute inset-x-0 top-0 h-[34rem] w-full object-cover opacity-35 mix-blend-screen"
        />
        <div className="radha-veil absolute inset-y-0 left-0 w-[140%]" />
      </div>

      <div className="sticky top-2 z-40 mx-auto w-full max-w-3xl px-3 pt-2">
        <div className="radha-scrollbar flex items-center gap-2 overflow-x-auto rounded-full border border-[#ffe5a3]/35 bg-[#14111d]/78 p-2 shadow-[0_16px_40px_rgba(0,0,0,0.3)] backdrop-blur-xl">
          {SCENES.map((scene) => (
            <button
              key={scene.id}
              type="button"
              onClick={() => scrollToScene(scene.id)}
              className={`radha-label shrink-0 rounded-full px-3 py-2 text-xs transition-all ${
                activeSceneId === scene.id
                  ? 'bg-[#f5cd62] text-[#321b12] shadow-[0_0_18px_rgba(245,205,98,0.38)]'
                  : 'text-[#fff1c7] hover:bg-white/10'
              }`}
            >
              {scene.shortName}
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {showShareToast && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="absolute left-1/2 top-16 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#f5cd62] px-4 py-2 text-sm font-semibold text-[#321b12] shadow-xl"
          >
            <CheckCircle2 className="h-4 w-4" /> Link copied
          </motion.div>
        )}
      </AnimatePresence>

      <main className="relative z-10 mx-auto flex w-full max-w-3xl flex-col gap-12 px-4 pb-14 pt-4 sm:px-6">
        <section
          id="radha-welcome"
          data-radha-scene
          data-scene-id="welcome"
          className="flex min-h-[84vh] items-center justify-center py-8"
        >
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75 }}
            className="radha-glass relative w-full overflow-hidden rounded-[2rem] p-5 text-center sm:p-8"
          >
            <img
              src={`${ASSET_BASE}/ganesha-blessing.webp`}
              alt=""
              aria-hidden="true"
              className="radha-float mx-auto mb-3 h-28 w-24 object-contain drop-shadow-[0_8px_22px_rgba(245,205,98,0.4)] sm:h-36 sm:w-28"
            />
            <div className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-[#ffe5a3]/45 bg-white/10 px-4 py-2 text-xs text-[#ffe9ad]">
              <Sparkles className="h-4 w-4" /> Shubh Vivah
            </div>
            <p className="radha-label text-xs text-[#ffc9d6]">{hostNames} invite you to celebrate</p>
            <h1 className="radha-display radha-gold-text mt-3 text-5xl leading-none sm:text-7xl">
              {groomName} <span className="text-3xl sm:text-4xl">&amp;</span> {brideName}
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#fff1c7] sm:text-base">{tagline}</p>

            <div className="mt-5 grid gap-3 rounded-3xl border border-[#ffe5a3]/30 bg-[#14111d]/50 p-4 text-left sm:grid-cols-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 shrink-0 text-[#f5cd62]" />
                <span className="text-sm">{eventDate || SCENES[1].defaultDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 shrink-0 text-[#f5cd62]" />
                <span className="text-sm">{eventTime}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-[#f5cd62]" />
                <span className="text-sm">{venueName}</span>
              </div>
            </div>

            <PhotoShowcase
              brideName={brideName}
              groomName={groomName}
              bridePhoto={bridePhoto}
              groomPhoto={groomPhoto}
              couplePhoto={couplePhoto}
            />

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setShowRsvpModal(true)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#f5cd62] px-5 py-3 text-sm font-bold text-[#321b12] shadow-[0_0_24px_rgba(245,205,98,0.32)] transition-transform hover:scale-[1.02] active:scale-95"
              >
                <Send className="h-4 w-4" /> RSVP
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#ffe5a3]/45 bg-white/10 px-5 py-3 text-sm font-semibold text-[#fff1c7] transition-colors hover:bg-white/15"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>

            <button
              type="button"
              onClick={() => scrollToScene('ganesha')}
              className="mx-auto mt-7 flex flex-col items-center text-xs text-[#ffe5a3]/85"
            >
              <span>Begin the celebration</span>
              <ChevronDown className="mt-1 h-5 w-5 animate-bounce" />
            </button>
          </motion.div>
        </section>

        {SCENES.map((scene, index) => (
          <section
            key={scene.id}
            id={`radha-${scene.id}`}
            data-radha-scene
            data-scene-id={scene.id}
            className="flex min-h-[76vh] items-center justify-center py-8"
          >
            <motion.article
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.26 }}
              transition={{ duration: 0.58 }}
              className="radha-glass grid w-full overflow-hidden rounded-[2rem] lg:grid-cols-[0.9fr_1.1fr]"
            >
              <div className={`relative min-h-[25rem] ${index % 2 ? 'lg:order-2' : ''}`}>
                <img src={scene.image} alt={scene.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#171021]/75 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 rounded-2xl border border-white/25 bg-black/35 p-3 backdrop-blur-md">
                  <p className="radha-label text-xs text-[#ffe5a3]">{scene.devotionalTitle}</p>
                  <p className="mt-1 text-sm text-white/90">{scene.copy}</p>
                </div>
              </div>

              <div className="flex flex-col justify-center p-5 sm:p-7">
                <div className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-[#ffe5a3]/40 bg-white/10 px-3 py-1.5 text-xs text-[#ffe5a3]">
                  <Heart className="h-3.5 w-3.5" /> {eventTitle}
                </div>
                <h2 className="radha-display radha-gold-text text-4xl leading-none sm:text-5xl">{scene.title}</h2>
                <p className="mt-3 text-sm leading-6 text-[#fff1c7]">{scene.copy}</p>

                <div className="mt-5 grid gap-3">
                  <div className="rounded-2xl border border-[#ffe5a3]/30 bg-[#14111d]/50 p-4">
                    <div className="flex items-center gap-2 text-[#ffe7a1]">
                      <Calendar className="h-4 w-4 text-[#f5cd62]" />
                      <span>{eventDate || scene.defaultDate}</span>
                    </div>
                    <div className="mt-2 flex items-center gap-2 text-[#ffe7a1]">
                      <Clock className="h-4 w-4 text-[#f5cd62]" />
                      <span>{eventTime || scene.defaultTime}</span>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#ffe5a3]/30 bg-[#14111d]/50 p-4">
                    <div className="flex items-start gap-2 text-[#ffe7a1]">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#f5cd62]" />
                      <div>
                        <p className="font-semibold">{venueName}</p>
                        <p className="text-sm text-[#fff1c7]/80">{venueAddress}</p>
                      </div>
                    </div>
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(`${venueName} ${venueAddress}`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-2 rounded-xl border border-[#ffe5a3]/35 bg-[#f5cd62]/18 px-3 py-2 text-sm text-[#ffe7a1] transition-colors hover:bg-[#f5cd62]/25"
                    >
                      <Navigation className="h-4 w-4" /> Directions
                    </a>
                  </div>
                </div>
              </div>
            </motion.article>
          </section>
        ))}

        {customVideoUrl && (
          <section className="flex min-h-[52vh] items-center justify-center py-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: false, amount: 0.3 }}
              className="radha-glass w-full rounded-[2rem] p-5 text-center sm:p-8"
            >
              <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full border border-[#ffe5a3]/40 bg-white/10 px-4 py-2 text-xs text-[#ffe5a3]">
                <Film className="h-4 w-4" /> Pre-Wedding Story
              </div>
              <h2 className="radha-display radha-gold-text text-4xl sm:text-5xl">Our Pre-Wedding Story &amp; Special Memories</h2>
              <div className="mt-5 overflow-hidden rounded-3xl border border-[#ffe5a3]/35 bg-black">
                <video src={customVideoUrl} controls playsInline className="max-h-[28rem] w-full object-contain" />
              </div>
            </motion.div>
          </section>
        )}

        <section className="flex min-h-[60vh] items-center justify-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            className="radha-glass w-full rounded-[2rem] p-5 text-center sm:p-8"
          >
            <Heart className="radha-soft-pulse mx-auto h-10 w-10 text-[#f5cd62]" />
            <h2 className="radha-display radha-gold-text mt-3 text-4xl sm:text-5xl">Awaiting Your Blessings</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-[#fff1c7]">
              Your presence will make this celebration complete for {groomName} and {brideName}.
            </p>
            <div className="mt-6 rounded-3xl border border-[#ffe5a3]/35 bg-[#14111d]/55 p-5 text-left">
              <p className="flex items-center gap-2 font-semibold text-[#ffe7a1]">
                <MapPin className="h-4 w-4 text-[#f5cd62]" /> {venueName}
              </p>
              <p className="mt-1 text-sm text-[#fff1c7]/80">{venueAddress}</p>
              <p className="mt-3 text-sm text-[#ffc9d6]">Hosted by {hostNames}</p>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={() => setShowRsvpModal(true)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#f5cd62] px-5 py-3 text-sm font-bold text-[#321b12] transition-transform hover:scale-[1.02] active:scale-95"
              >
                <Send className="h-4 w-4" /> Confirm RSVP
              </button>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(`${venueName} ${venueAddress}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#ffe5a3]/45 bg-white/10 px-5 py-3 text-sm font-semibold text-[#fff1c7] transition-colors hover:bg-white/15"
              >
                <Navigation className="h-4 w-4" /> Open Map
              </a>
            </div>
          </motion.div>
        </section>
      </main>

      <AnimatePresence>
        {showRsvpModal && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-[#050407]/78 p-4 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              className="radha-glass w-full max-w-md rounded-[2rem] p-6 text-center"
              role="dialog"
              aria-modal="true"
              aria-label="RSVP confirmation"
            >
              <button
                type="button"
                onClick={() => setShowRsvpModal(false)}
                className="ml-auto flex h-9 w-9 items-center justify-center rounded-full border border-[#ffe5a3]/35 bg-white/10 text-[#ffe7a1] transition-colors hover:bg-white/15"
                aria-label="Close RSVP"
              >
                <X className="h-4 w-4" />
              </button>
              <Heart className="mx-auto mt-2 h-9 w-9 text-[#f5cd62]" />
              <h3 className="radha-display radha-gold-text mt-3 text-4xl">RSVP Confirmation</h3>
              <p className="mt-3 text-sm leading-6 text-[#fff1c7]">
                Please confirm your presence for the wedding celebration of {groomName} and {brideName}.
              </p>
              <div className="mt-5 grid gap-3 text-left">
                <label className="text-sm text-[#ffe7a1]">
                  Your name
                  <input
                    type="text"
                    className="mt-1 w-full rounded-2xl border border-[#ffe5a3]/35 bg-[#14111d]/80 px-4 py-3 text-[#fff6dc] outline-none focus:border-[#f5cd62]"
                    placeholder="Guest name"
                  />
                </label>
                <label className="text-sm text-[#ffe7a1]">
                  Guests
                  <select className="mt-1 w-full rounded-2xl border border-[#ffe5a3]/35 bg-[#14111d]/80 px-4 py-3 text-[#fff6dc] outline-none focus:border-[#f5cd62]">
                    <option>1 guest</option>
                    <option>2 guests</option>
                    <option>Family group</option>
                  </select>
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowRsvpModal(false)}
                className="mt-5 w-full rounded-2xl bg-[#f5cd62] px-5 py-3 text-sm font-bold text-[#321b12] transition-transform hover:scale-[1.01] active:scale-95"
              >
                Submit RSVP
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

registry.register({
  metadata: META,
  component: VideoRadhaKrishnaTemplate,
});
